import { createApp } from 'vue'
import { createPinia } from 'pinia'
import { createI18n } from 'vue-i18n'

// Vuetify
import 'vuetify/styles'
import { createVuetify } from 'vuetify'
import * as components from 'vuetify/components'
import * as directives from 'vuetify/directives'
import '@mdi/font/css/materialdesignicons.css'

// Router
import router from './router'

// App
import App from './App.vue'

// Styles
import './styles/main.css'

// i18n
import messages from './locales'

// Función para detectar el idioma del navegador
const detectBrowserLanguage = () => {
  // Primero intentar obtener del localStorage
  const saved = localStorage.getItem('preferred-language')
  if (saved && ['en', 'es', 'pt'].includes(saved)) {
    return saved
  }
  
  // Obtener el idioma del navegador
  const browserLang = navigator.language || navigator.languages?.[0] || 'en'
  
  // Extraer el código de idioma principal (ej: 'en' de 'en-US')
  const primaryLang = browserLang.split('-')[0]
  
  // Verificar si el idioma está soportado
  const supportedLanguages = Object.keys(messages)
  if (supportedLanguages.includes(primaryLang)) {
    return primaryLang
  }
  
  // Si no está soportado, usar inglés como fallback
  return 'en'
}

const i18n = createI18n({
  legacy: false,
  locale: detectBrowserLanguage(),
  fallbackLocale: 'en',
  messages,
})

const getSavedTheme = (): string => {
  const saved = localStorage.getItem('preferred-theme')
  return saved === 'dark' ? 'dark' : 'light'
}

const vuetify = createVuetify({
  components,
  directives,
  theme: {
    defaultTheme: getSavedTheme(),
    themes: {
      light: {
        dark: false,
        colors: {
          background: '#f5f7fb',
          surface: '#ffffff',
          'surface-variant': '#eef0f5',
          'on-surface': '#1f2937',
          'on-surface-variant': '#475569',
          primary: '#667eea',
          'primary-darken-1': '#5468c4',
          secondary: '#764ba2',
          error: '#d32f2f',
          info: '#1976d2',
          success: '#2e7d32',
          warning: '#ef6c00',
        },
      },
      dark: {
        dark: true,
        colors: {
          background: '#0b0d12',
          surface: '#1e222b',
          'surface-bright': '#2a2f3a',
          'surface-variant': '#2e333f',
          'on-surface': '#e6e8ee',
          'on-surface-variant': '#aab2c0',
          primary: '#8fa1ff',
          'primary-darken-1': '#7185ee',
          secondary: '#b08bd6',
          error: '#ef5350',
          info: '#42a5f5',
          success: '#66bb6a',
          warning: '#ffa726',
        },
      },
    },
  },
})

const app = createApp(App)

app.use(createPinia())
app.use(router)
app.use(vuetify)
app.use(i18n)

// Initialize auth before mounting the app
import { useAuthStore } from './stores/auth'

// Mount the app first
app.mount('#app')

// Function to hide initial loading screen
const hideInitialLoading = () => {
  const initialLoading = document.getElementById('initial-loading')
  if (initialLoading) {
    initialLoading.style.display = 'none'
  }
}

// Then initialize auth (this will trigger the loading screen)
const authStore = useAuthStore()

// Initialize auth and hide initial loading when done
authStore.initializeAuth().then(() => {
  // Hide the initial loading screen once auth is initialized
  hideInitialLoading()
}).catch(() => {
  // Even if auth fails, hide the initial loading screen
  hideInitialLoading()
})
